import StatusChangedMsg from '@apptypes/WebsocketUserStatus.ts';
import { WebSocketClientChangedWatcher } from '@components/websocket-watchers/websocket-client-changed-watcher.tsx';
import { WebSocketGambleTimeReachedWatcher } from '@components/websocket-watchers/websocket-gamble-time-reached-watcher.tsx';
import { WebSocketLogoutWatcher } from '@components/websocket-watchers/websocket-logout-watcher.tsx';
import { WebSocketPlayerAuthInfoWatcher } from '@components/websocket-watchers/websocket-player-auth-info-watcher.tsx';
import { WebSocketPlayerBalanceChangedWatcher } from '@components/websocket-watchers/websocket-player-balance-changed-watcher.tsx';
import { WebSocketBankAccountWatcher } from '@components/websocket-watchers/websocket-player-bank-account-changed-watcher.tsx';
import { WebSocketPlayerDepositThresholdWatcher } from '@components/websocket-watchers/websocket-player-deposit-threshold-watcher.tsx';
import { WebSocketPlayerDisabledWatcher } from '@components/websocket-watchers/websocket-player-disabled-watcher.tsx';
import { WebSocketStatusChangedWatcher } from '@components/websocket-watchers/websocket-player-status-change-watcher.tsx';
import { useAuth } from '@hooks/useAuth.tsx';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Socket, io } from 'socket.io-client';

type Action = StatusChangedMsg['action'];
type LatestByAction = Partial<Record<Action, StatusChangedMsg>>;

type SocketContextValue = {
  socket: Socket | null;
  connected: boolean;

  // raw stream (last N)
  messages: StatusChangedMsg[];

  // latest message per action
  latest: LatestByAction;

  // typed helper: get latest by action (returns narrowed type)
  getLatest: <A extends Action>(
    action: A,
  ) => Extract<StatusChangedMsg, { action: A }> | undefined;

  clear: () => void;
};

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);

  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<StatusChangedMsg[]>([]);
  const [latest, setLatest] = useState<LatestByAction>({});

  const socketUrl = window.__config__?.componentSettings?.v2Auth;

  const clear = useCallback(() => {
    setMessages([]);
    setLatest({});
  }, []);

  const onIncoming = useCallback((msg: StatusChangedMsg) => {
    setMessages(prev => [...prev, msg].slice(-200));
    setLatest(prev => ({ ...prev, [msg.action]: msg }));
  }, []);

  useEffect(() => {
    if (!socketUrl) return;

    // If not authenticated, ensure socket is fully gone
    if (!user?.logged_in || !user?.token) {
      socketRef.current?.removeAllListeners();
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
      clear();
      return;
    }

    // Create socket ONCE for this url/token combination
    if (!socketRef.current) {
      socketRef.current = io(socketUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: false, // ✅ important
        forceNew: true, // ✅ avoids reusing an old manager
      });
    }

    const s = socketRef.current;

    const handleConnect = () => {
      console.log('Socket connected');
      s.emit('join', user.token);
      setConnected(true);
    };

    const handleDisconnect = () => setConnected(false);
    const handleError = () => setConnected(false);

    // bind listeners (first remove to avoid duplicates)
    s.off('connect', handleConnect);
    s.off('disconnect', handleDisconnect);
    s.off('connect_error', handleError);
    s.off('message', onIncoming);

    s.on('connect', handleConnect);
    s.on('disconnect', handleDisconnect);
    s.on('connect_error', handleError);
    s.on('message', onIncoming);

    // connect only if not already connected/connecting
    if (!s.connected) s.connect();

    return () => {
      s.off('message', onIncoming);
      s.off('connect', handleConnect);
      s.off('disconnect', handleDisconnect);
      s.off('connect_error', handleError);

      s.disconnect();
      socketRef.current = null;
    };
  }, [socketUrl, user?.logged_in, user?.token, onIncoming, clear]);

  const getLatest = useCallback(
    <A extends Action>(action: A) =>
      latest[action] as Extract<StatusChangedMsg, { action: A }> | undefined,
    [latest],
  );

  const value = useMemo(
    () => ({
      socket: socketRef.current,
      connected,
      messages,
      latest,
      getLatest,
      clear,
    }),
    [connected, messages, latest, getLatest, clear],
  );

  useEffect(() => {
    console.log('SocketProvider: mounted');
  }, []);

  return (
    <SocketContext.Provider value={value}>
      {children}
      <WebSocketPlayerAuthInfoWatcher />
      <WebSocketClientChangedWatcher />
      <WebSocketGambleTimeReachedWatcher />
      <WebSocketLogoutWatcher />
      <WebSocketPlayerBalanceChangedWatcher />
      <WebSocketBankAccountWatcher />
      <WebSocketPlayerDepositThresholdWatcher />
      <WebSocketPlayerDisabledWatcher />
      <WebSocketStatusChangedWatcher />
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within <SocketProvider />');
  return ctx;
}
